import ApplicationError, { ErrorSeverity } from 'es/app/business-logic/ApplicationError';
import HttpOperationError from 'es/app/business-logic/HttpOperationError';
import { createl10n } from 'es/i18n';
import { GraphQLFormattedError } from 'graphql';
const t = createl10n('core', 'net.apolloProvider');

export const extractGraphQlErrors = (graphQLErrors: readonly GraphQLFormattedError[]): string[] => {
    // Prune out the 'GraphQL' part of the header to make a user-friendly error message.
    // e.g. "GraphQL.ExecutionError: The date range provided is too large." will become
    // "The date range provided is too large.".
    const errorMessages = graphQLErrors.map(error => error.message && error.message.replace(/^GraphQL[^:]*?:\s*/, ''));

    // Get distinct error messages only. This is to cater for scenarios such as when you
    // ask for 5 monitor variables with a date range that's too large - the net result
    // is 5 copies of "GraphQL.ExecutionError: The date range provided is too large.".
    const distinctErrorMessages = new Set<string>(errorMessages);
    return [...distinctErrorMessages.values()];
};

export function raiseGraphErrorIfUnhandled(errors: readonly GraphQLFormattedError[], operationName: string) {
    const details = extractGraphQlErrors(errors).join('\n') || t('unknownError');
    const errorText = operationName ? t('errors.namedOperationFailed', { operationName }) : t('errors.operationFailed');

    const error = new HttpOperationError(errorText, details);

    // If nothing marks the error as handled by the time the queue gets back to processing this handler, then
    // raise this as an application error.
    setTimeout(() => {
        if (error.handled) return;
        ApplicationError.raiseError(error.message, details, 'GraphQL', ErrorSeverity.Error);
    });

    throw error;
}

export function raiseGraphWarning(errors: readonly GraphQLFormattedError[], operationName: string) {
    const details = extractGraphQlErrors(errors).join('\n') || t('unknownError');
    const errorText = operationName
        ? t('warnings.errorsRaisedForNamedQuery', { operationName })
        : t('warnings.errorsRaisedForQuery');

    ApplicationError.raiseError(errorText, details, 'GraphQL', ErrorSeverity.Warning);
}
